"use client";

import React, { useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Upload } from "lucide-react";
import { useForm } from "react-hook-form";
import { z, type ZodTypeAny } from "zod";
import {
  BlockWrapper,
  Button,
  Checkbox,
  H2,
  Input,
  MarkdownRenderer,
  SectionTag,
  SmallText,
  Form as FormComponent,
  FormControl,
  FormField as FormFieldComponent,
  FormItem,
  FormLabel,
  FormMessage,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components";
import { ImageDataType } from "@/shared/types";
import type { Form, FormField } from "@/lib/directus";
import {
  DEFAULT_FORM_FIELD_REQUIRED_ERROR,
  DEFAULT_FORM_SERVER_ERROR_MESSAGE,
  DEFAULT_FORM_SUBMIT_LABEL,
} from "@/shared/CONSTANTS";
import { submitForm } from "@/lib/actions/formActions";
import type { BlockSectionBg } from "./common/types";
import {
  getSectionBorderClass,
  getSectionSurfaceClass,
  sectionBgToWrapper,
} from "./blockSection";
import { resolveFormTheme } from "./formTheme";
import { cn } from "@/lib/utils";

const VALID_FIELD_TYPES = [
  "text",
  "textarea",
  "checkbox",
  "checkbox_group",
  "radio",
  "file",
  "select",
  "hidden",
] as const;

type FieldType = (typeof VALID_FIELD_TYPES)[number];

interface ValidFormField extends Omit<FormField, "name" | "type"> {
  name: string;
  type: FieldType;
}

const getValidatorValue = (validator: string): number =>
  parseInt(validator.split(":").reverse()[0], 10);

function getValidationRules(
  rulesStr: string,
  isRequired: boolean,
  fieldType: FieldType
): ZodTypeAny {
  if (fieldType === "file") {
    if (isRequired) {
      return z.any().refine(
        (val) => typeof File !== "undefined" && val instanceof File,
        { message: DEFAULT_FORM_FIELD_REQUIRED_ERROR }
      );
    }
    return z.any().nullable().optional();
  }

  if (!rulesStr || rulesStr.trim() === "") {
    if (isRequired) {
      return z.string().min(1, DEFAULT_FORM_FIELD_REQUIRED_ERROR);
    }
    return z.union([z.string(), z.literal("")]);
  }

  const rulesArr = rulesStr
    .split("|")
    .map((rule) => rule.trim())
    .filter(Boolean);

  let zodString = z.string();

  if (isRequired) {
    zodString = zodString.min(1, DEFAULT_FORM_FIELD_REQUIRED_ERROR);
  }

  zodString = rulesArr.reduce((accumulator, rule) => {
    const cleanRule = rule.replace(/[^A-Za-z]/g, "");

    switch (cleanRule) {
      case "email":
        return accumulator.email("Некорректный email адрес");
      case "url":
        return accumulator.url("Некорректный URL адрес");
      case "min":
        return accumulator.min(
          getValidatorValue(rule),
          `Минимальная длина: ${getValidatorValue(rule)} символов`
        );
      case "max":
        return accumulator.max(
          getValidatorValue(rule),
          `Максимальная длина: ${getValidatorValue(rule)} символов`
        );
      case "length":
        return accumulator.length(
          getValidatorValue(rule),
          `Длина должна быть ${getValidatorValue(rule)} символов`
        );
      default:
        return accumulator;
    }
  }, zodString);

  if (isRequired) {
    return zodString;
  }

  return z.union([z.literal(""), zodString]);
}

function isValidFieldType(type: string | null): type is FieldType {
  return (
    type !== null &&
    VALID_FIELD_TYPES.includes(type as (typeof VALID_FIELD_TYPES)[number])
  );
}

function customZodResolver<T extends z.ZodType>(schema: T) {
  return async (values: unknown) => {
    try {
      const result = schema.safeParse(values);

      if (result.success) {
        return { values: result.data, errors: {} };
      }

      const errors: Record<string, { type: string; message: string }> = {};
      for (const issue of result.error.issues) {
        const path = issue.path.join(".");
        if (path) {
          errors[path] = { type: issue.code, message: issue.message };
        }
      }
      return { values: {}, errors };
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Validation error:", error);
      }
      return {
        values: {},
        errors: { root: { type: "manual", message: "Ошибка валидации формы" } },
      };
    }
  };
}

function getValidFields(fields: (string | FormField)[]): ValidFormField[] {
  return fields
    .filter((field): field is FormField => typeof field === "object")
    .filter(
      (field): field is ValidFormField =>
        !!field.name && isValidFieldType(field.type)
    )
    .sort((fieldA, fieldB) =>
      fieldA.sort && fieldB.sort ? fieldA.sort - fieldB.sort : 0
    );
}

function getFieldWidthClass(width: FormField["width"]): string {
  switch (width) {
    case "100":
      return "col-span-full";
    case "67":
      return "col-span-full md:col-span-2";
    case "50":
      return "col-span-full md:col-span-1";
    case "33":
      return "col-span-full md:col-span-1";
    default:
      return "col-span-full";
  }
}

export type FormBlockVariant = "form_left" | "form_right";

export interface FormBlockProps {
  title?: string;
  description?: string;
  tagline?: string;
  image?: ImageDataType | null;
  formData?: Form | null;
  privacyPolicyUrl?: string | null;
  personalDataAgreementUrl?: string | null;
  variant?: FormBlockVariant | string | null;
  notificationEmail?: string | null;
  successRedirectUrl?: string | null;
  showPromoOnMobile?: boolean;
  sectionBg?: BlockSectionBg;
  showTopBorder?: boolean;
  showBottomBorder?: boolean;
  anchorId?: string | null;
}

const DEFAULT_TITLE = "Получите бесплатный расчёт";
const DEFAULT_DESCRIPTION =
  "Расскажите о вашем проекте, и мы свяжемся с вами в ближайшее время. Гарантируем точный расчёт стоимости и сроков.";
const CONSENT_REQUIRED_ERROR =
  "Необходимо дать согласие на обработку персональных данных";
const DEFAULT_PERSONAL_DATA_URL = "/personalnye-dannye";

const promoPaddingClass =
  "px-4 py-6 md:px-6 md:pt-10 md:pb-12 desktop:p-10";
const formPaddingClass =
  "px-4 py-6 md:px-6 md:pt-10 md:pb-12 desktop:p-10";

const consentCheckboxClass =
  "h-4 w-4 shrink-0 [&_svg]:size-4";

const consentRowClass = "flex cursor-pointer items-center gap-4";

const formErrorSlotClass = "font-sans text-xs leading-4";

const mobileFormFooterClass =
  "flex flex-col gap-4 border-t border-border p-4 md:px-6";

const formImageClass = "object-cover object-top";

function getFormImageContainerClass(layout: "desktop" | "mobile"): string {
  if (layout === "mobile") {
    return "relative w-full overflow-hidden aspect-[375/300] md:aspect-[768/350]";
  }

  return "relative w-full min-h-[280px] flex-1 overflow-hidden";
}

const formFieldInputClass =
  "w-full rounded-none border-0 border-x-0 border-t-0 border-b !bg-transparent px-0 py-2 font-sans text-base leading-none shadow-none outline-none ring-0 transition-colors focus:outline-none focus:ring-0 focus-visible:ring-0";

const promoDescriptionClass =
  "font-sans text-base leading-[1.2] text-primary-foreground [&_a]:text-primary-foreground [&_a]:underline [&_li]:mt-0 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:!text-primary-foreground [&_p+p]:mt-0 [&_strong]:!text-primary-foreground [&_ul]:list-disc [&_ul]:pl-6";

interface FormFileControlProps {
  id: string;
  fileName: string;
  disabled: boolean;
  accept?: string;
  buttonClassName: string;
  onFileSelect: (file: File | null) => void;
}

interface FormFileDropzoneProps extends FormFileControlProps {
  hintClassName: string;
  dropzoneBorderClassName: string;
}

function FormFileButton({
  id,
  fileName,
  disabled,
  accept,
  buttonClassName,
  onFileSelect,
}: FormFileControlProps) {
  return (
    <div className="flex flex-col gap-2">
      <input
        type="file"
        id={`file-input-${id}`}
        className="hidden"
        disabled={disabled}
        accept={accept}
        onChange={(event) => {
          const file = event.target.files?.[0] || null;
          onFileSelect(file);
        }}
      />
      <Button
        type="button"
        variant="ghost"
        disabled={disabled}
        onClick={() => {
          document.getElementById(`file-input-${id}`)?.click();
        }}
        className={cn("justify-start gap-2 px-0 normal-case", buttonClassName)}
      >
        <Upload className="size-5" />
        <span className="font-sans text-base font-normal">{fileName}</span>
      </Button>
    </div>
  );
}

function FormFileDropzone({
  id,
  fileName,
  disabled,
  accept,
  buttonClassName,
  hintClassName,
  dropzoneBorderClassName,
  onFileSelect,
}: FormFileDropzoneProps) {
  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = (file: File | null) => {
    onFileSelect(file);
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-2 rounded border border-dashed px-4 py-6 transition-colors",
        isDragging ? "border-primary bg-primary/5" : dropzoneBorderClassName,
        disabled && "opacity-60"
      )}
      onDragEnter={(event) => {
        event.preventDefault();
        if (!disabled) {
          setIsDragging(true);
        }
      }}
      onDragOver={(event) => {
        event.preventDefault();
        if (!disabled) {
          setIsDragging(true);
        }
      }}
      onDragLeave={(event) => {
        event.preventDefault();
        setIsDragging(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        setIsDragging(false);
        if (disabled) return;

        const file = event.dataTransfer.files?.[0] || null;
        handleFileChange(file);
      }}
    >
      <input
        type="file"
        id={`file-input-${id}`}
        className="hidden"
        disabled={disabled}
        accept={accept}
        onChange={(event) => {
          const file = event.target.files?.[0] || null;
          handleFileChange(file);
        }}
      />
      <Button
        type="button"
        variant="ghost"
        disabled={disabled}
        onClick={() => {
          document.getElementById(`file-input-${id}`)?.click();
        }}
        className={cn("justify-start gap-2 px-0 normal-case", buttonClassName)}
      >
        <Upload className="size-5" />
        <span className="font-sans text-base font-normal">{fileName}</span>
      </Button>
      <SmallText className={hintClassName}>
        Перетащите файл сюда или нажмите для выбора
      </SmallText>
    </div>
  );
}

export const FormBlock: React.FC<FormBlockProps> = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  tagline,
  image,
  formData,
  personalDataAgreementUrl,
  variant = "form_right",
  notificationEmail,
  successRedirectUrl,
  showPromoOnMobile = true,
  sectionBg = "light",
  showTopBorder = false,
  showBottomBorder = false,
  anchorId = null,
}) => {
  const theme = resolveFormTheme(sectionBg);
  const borderClass = getSectionBorderClass(sectionBg);
  const sectionSurfaceClass = getSectionSurfaceClass(sectionBg);
  const isFormLeft = variant === "form_left";
  const hasImage = Boolean(image);
  const personalDataUrl = personalDataAgreementUrl || DEFAULT_PERSONAL_DATA_URL;

  const validFields = useMemo(
    () => (formData?.fields ? getValidFields(formData.fields) : []),
    [formData]
  );

  const initialFormState = useMemo(
    () =>
      validFields.reduce(
        (accumulator, field) => {
          if (!field.name) return accumulator;
          const defaultValue = field.type === "file" ? null : "";
          return { ...accumulator, [field.name]: defaultValue };
        },
        {} as Record<string, string | File | null>
      ),
    [validFields]
  );

  const formSchema = useMemo(
    () =>
      z.object(
        validFields.reduce(
          (accumulator, field) =>
            field.name
              ? {
                  ...accumulator,
                  [field.name]: getValidationRules(
                    field.validation || "",
                    Boolean(field.required),
                    field.type
                  ),
                }
              : accumulator,
          {} as Record<string, ZodTypeAny>
        )
      ),
    [validFields]
  );

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: customZodResolver(formSchema),
    defaultValues: initialFormState,
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const router = useRouter();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);

  const {
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = form;

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!formData?.id) return;

    if (!consentAccepted) {
      return;
    }

    setConsentError(null);

    const fields = validFields.map((field) => {
      const value = values[field.name];
      let fieldValue = "";
      let file: File | null = null;

      if (
        field.type === "file" &&
        typeof File !== "undefined" &&
        value instanceof File
      ) {
        file = value;
        fieldValue = value.name;
      } else if (value !== null && value !== undefined && value !== "") {
        fieldValue = String(value);
      }

      return { fieldId: field.id, fieldName: field.name, value: fieldValue, file };
    });

    const result = await submitForm(formData.id, fields, {
      notificationEmail,
    });

    if (!result.success) {
      setError("root.serverError", {
        message: DEFAULT_FORM_SERVER_ERROR_MESSAGE,
      });
      return;
    }

    reset();
    setConsentAccepted(false);

    const redirectUrl = successRedirectUrl || formData.success_redirect_url;

    if (redirectUrl) {
      router.push(redirectUrl);
      return;
    }

    if (formData.on_success === "message" && formData.success_message) {
      setSuccessMessage(formData.success_message);
    }
  };

  const onError = (validationErrors: typeof form.formState.errors) => {
    if (process.env.NODE_ENV !== "production") {
      console.log("Form validation errors:", validationErrors);
    }
  };

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (!consentAccepted) {
      setConsentError(CONSENT_REQUIRED_ERROR);
    } else {
      setConsentError(null);
    }

    void form.handleSubmit(onSubmit, onError)(event);
  };

  const inputBase = cn(
    formFieldInputClass,
    theme.formInput,
    theme.formPlaceholder,
    theme.formInputBorder
  );

  const labelBase = cn("font-sans text-xs font-normal leading-normal", theme.formLabel);
  const errorClass = "text-primary";
  const errorInputClass = "border-primary text-primary placeholder:text-primary";

  const renderFieldInput = (
    field: ValidFormField,
    formField: {
      value: unknown;
      onChange: (value: unknown) => void;
      onBlur: () => void;
      name: string;
      ref: React.Ref<unknown>;
    },
    hasError: boolean
  ) => {
    const { type, id, placeholder, choices, validation } = field;
    const fieldErrorClass = hasError ? errorInputClass : "";

    switch (type) {
      case "textarea":
        return (
          <Textarea
            readOnly={isSubmitting}
            placeholder={placeholder || ""}
            className={cn(inputBase, fieldErrorClass, "min-h-16 resize-none desktop:min-h-24")}
            value={String(formField.value || "")}
            onChange={formField.onChange}
            onBlur={formField.onBlur}
            name={formField.name}
            ref={formField.ref as React.Ref<HTMLTextAreaElement>}
          />
        );

      case "select":
        return (
          <Select
            disabled={isSubmitting}
            value={String(formField.value || "") || undefined}
            onValueChange={(value) => formField.onChange(value || "")}
          >
            <SelectTrigger
              className={cn(inputBase, fieldErrorClass, "shadow-none [&>svg]:opacity-60")}
            >
              <SelectValue placeholder={placeholder || "Выберите вариант"} />
            </SelectTrigger>
            <SelectContent>
              {choices && choices.length > 0 ? (
                choices.map((choice) => (
                  <SelectItem key={choice.value} value={choice.value}>
                    {choice.text}
                  </SelectItem>
                ))
              ) : (
                <SelectItem value="" disabled>
                  Нет доступных опций
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        );

      case "file": {
        const fileName =
          typeof File !== "undefined" && formField.value instanceof File
            ? formField.value.name
            : placeholder || "Выберите файл";

        return (
          <div className="w-full">
            <div className="hidden desktop:block">
              <FormFileDropzone
                id={id}
                fileName={fileName}
                disabled={isSubmitting}
                accept={validation || undefined}
                buttonClassName={theme.formFileButton}
                hintClassName={theme.formHint}
                dropzoneBorderClassName={theme.formDropzoneBorder}
                onFileSelect={(file) => formField.onChange(file)}
              />
            </div>
            <div className="desktop:hidden">
              <FormFileButton
                id={id}
                fileName={fileName}
                disabled={isSubmitting}
                accept={validation || undefined}
                buttonClassName={theme.formFileButton}
                onFileSelect={(file) => formField.onChange(file)}
              />
            </div>
          </div>
        );
      }

      default:
        return (
          <Input
            readOnly={isSubmitting}
            placeholder={placeholder || ""}
            type={type === "hidden" ? "hidden" : "text"}
            className={cn(inputBase, fieldErrorClass)}
            value={String(formField.value || "")}
            onChange={formField.onChange}
            onBlur={formField.onBlur}
            name={formField.name}
            ref={formField.ref as React.Ref<HTMLInputElement>}
          />
        );
    }
  };

  const renderField = (field: ValidFormField) => {
    const { label, id, name, width } = field;

    return (
      <div key={id} className={getFieldWidthClass(width)}>
        <FormFieldComponent
          control={form.control}
          name={name}
          render={({ field: formField, fieldState }) => (
            <FormItem className="flex flex-col gap-2">
              {label && (
                <FormLabel className={cn(labelBase, fieldState.error && errorClass)}>
                  {label}
                  {field.required && <span className="ml-0.5 text-primary">*</span>}
                </FormLabel>
              )}
              <FormControl>
                {renderFieldInput(field, formField, !!fieldState.error)}
              </FormControl>
              <div className={formErrorSlotClass}>
                {fieldState.error ? (
                  <FormMessage className={cn("font-sans text-xs", errorClass)} />
                ) : (
                  <div className="hidden min-h-4 desktop:block" aria-hidden="true" />
                )}
              </div>
            </FormItem>
          )}
        />
      </div>
    );
  };

  const renderConsent = (options?: { compact?: boolean }) => {
    const hasConsentError = Boolean(consentError);
    const isCompact = options?.compact ?? false;

    return (
      <div className="flex flex-col gap-2">
        <label htmlFor="form-consent" className={consentRowClass}>
          <Checkbox
            id="form-consent"
            checked={consentAccepted}
            onCheckedChange={(checked) => {
              setConsentAccepted(checked === true);
              if (checked === true) {
                setConsentError(null);
              }
            }}
            className={cn(
              consentCheckboxClass,
              theme.checkbox,
              hasConsentError && "border-primary data-[state=unchecked]:border-primary"
            )}
          />
          <SmallText
            className={cn(
              "flex-1 leading-[1.2]",
              hasConsentError ? errorClass : theme.consentText
            )}
          >
            {isCompact ? (
              <>
                Согласен на{" "}
                <Link
                  href={personalDataUrl}
                  className={cn(
                    "underline",
                    hasConsentError ? errorClass : theme.consentLink
                  )}
                >
                  обработку персональных данных
                </Link>
              </>
            ) : (
              <>
                Нажимая кнопку «Отправить», вы подтверждаете свое согласие на обработку{" "}
                <Link
                  href={personalDataUrl}
                  className={cn(
                    "underline",
                    hasConsentError ? errorClass : theme.consentLink
                  )}
                >
                  персональных данных
                </Link>
              </>
            )}
          </SmallText>
        </label>
        {hasConsentError ? (
          <p className={cn("font-sans text-xs leading-4", errorClass)}>
            {consentError}
          </p>
        ) : (
          <div className="hidden min-h-4 desktop:block" aria-hidden="true" />
        )}
      </div>
    );
  };

  const renderSubmitButton = (options?: { fullWidth?: boolean }) => (
    <Button
      type="submit"
      disabled={isSubmitting || !!successMessage}
      variant="default"
      className={cn("uppercase", options?.fullWidth ? "w-full" : "w-fit")}
    >
      {isSubmitting ? "Отправка..." : formData?.submit_label || DEFAULT_FORM_SUBMIT_LABEL}
    </Button>
  );

  const renderFormPanel = (layout: "mobile" | "desktop") => {
    if (layout === "desktop") {
      return (
        <div
          className={cn(
            sectionSurfaceClass,
            theme.formInput,
            formPaddingClass,
            borderClass,
            hasImage && "border-b",
            "desktop:border-b-0"
          )}
        >
          <FormComponent {...form}>
            <form onSubmit={handleFormSubmit} className="flex flex-col gap-4 md:gap-6" noValidate>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {validFields.map(renderField)}
              </div>

              {errors.root?.serverError?.message && (
                <p className={cn("font-sans text-sm", errorClass)}>
                  {errors.root.serverError.message}
                </p>
              )}

              {successMessage && (
                <p className={cn("font-sans text-sm", theme.successText)}>
                  {successMessage}
                </p>
              )}

              <div className="flex flex-col gap-6">
                {renderConsent()}
                {renderSubmitButton()}
              </div>
            </form>
          </FormComponent>
        </div>
      );
    }

    return (
      <div
        className={cn(
          "w-full min-w-0",
          sectionSurfaceClass,
          theme.formInput,
          borderClass,
          hasImage && "border-b",
          "desktop:border-b-0"
        )}
      >
        <FormComponent {...form}>
          <form onSubmit={handleFormSubmit} className="flex flex-col" noValidate>
            <div className={cn("flex flex-col gap-4 md:gap-6", formPaddingClass)}>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {validFields.map(renderField)}
              </div>

              {errors.root?.serverError?.message && (
                <p className={cn("font-sans text-sm", errorClass)}>
                  {errors.root.serverError.message}
                </p>
              )}

              {successMessage && (
                <p className={cn("font-sans text-sm", theme.successText)}>
                  {successMessage}
                </p>
              )}
            </div>

            <div
              className={cn(
                mobileFormFooterClass,
                sectionSurfaceClass,
                "w-full"
              )}
            >
              {renderConsent({ compact: true })}
              {renderSubmitButton({ fullWidth: true })}
            </div>
          </form>
        </FormComponent>
      </div>
    );
  };

  const promoPanel = (options?: { stackedAboveImage?: boolean }) => (
    <div
      className={cn(
        "flex flex-col gap-2 bg-primary text-primary-foreground md:gap-4",
        promoPaddingClass,
        borderClass,
        "border-b",
        options?.stackedAboveImage && "desktop:border-b-0"
      )}
    >
      {tagline && (
        <SectionTag label={tagline} variant="onTinted" className="uppercase" />
      )}
      {title && (
        <H2 className="text-heading-2-sm text-primary-foreground md:text-heading-2">
          {title}
        </H2>
      )}
      {description && (
        <MarkdownRenderer content={description} className={promoDescriptionClass} />
      )}
    </div>
  );

  const imagePanel = (options?: { layout: "desktop" | "mobile" }) => {
    if (!image) return null;

    const layout = options?.layout ?? "desktop";

    return (
      <div className={getFormImageContainerClass(layout)}>
        <Image
          src={image.src}
          alt={image.alt || title || ""}
          title={image.title ?? undefined}
          fill
          sizes={layout === "mobile" ? "100vw" : "33vw"}
          className={formImageClass}
        />
      </div>
    );
  };

  const promoImageColumn = (
    <div
      className={cn(
        "flex w-full min-w-0 flex-col gap-0 desktop:w-1/3 desktop:self-stretch",
        !isFormLeft && borderClass,
        !isFormLeft && "border-r"
      )}
    >
      {promoPanel({ stackedAboveImage: hasImage })}
      {imagePanel({ layout: "desktop" })}
    </div>
  );

  const formColumnDesktop = (
    <div
      className={cn(
        "hidden w-full desktop:flex desktop:w-2/3 desktop:flex-col",
        sectionSurfaceClass,
        isFormLeft && borderClass,
        isFormLeft && "border-r"
      )}
    >
      {renderFormPanel("desktop")}
    </div>
  );

  return (
    <BlockWrapper bgColor={sectionBgToWrapper(sectionBg)} noPadding>
      <div
        id={anchorId ?? undefined}
        className={cn(
          "w-full min-w-0 scroll-mt-[var(--header-height)]",
          showTopBorder && cn("border-t", borderClass, "-mt-px"),
          showBottomBorder && cn("border-b", borderClass, "-mb-px")
        )}
      >
        {/* Mobile & tablet: promo → form → image */}
        <div className="flex flex-col desktop:hidden">
          {showPromoOnMobile && promoPanel()}
          {renderFormPanel("mobile")}
          {hasImage && imagePanel({ layout: "mobile" })}
        </div>

        {/* Desktop: 1/3 promo+image | 2/3 form (variant swaps order) */}
        <div className="hidden desktop:flex desktop:items-stretch">
          {isFormLeft ? (
            <>
              {formColumnDesktop}
              {promoImageColumn}
            </>
          ) : (
            <>
              {promoImageColumn}
              {formColumnDesktop}
            </>
          )}
        </div>
      </div>
    </BlockWrapper>
  );
};
